<template>
  <div class="login-form-card">
    <div 
      class="login-form-card__logo-box" 
      v-if="organizationLogoUrl"
      :style="logoBoxStyle"
    >
      <div class="login-form-card__logo-wrapper">
        <img 
          :src="organizationLogoUrl" 
          alt="Organization Logo"
          class="login-form-card__logo"
        />
      </div>
    </div>
    <div class="login-form-card__header">
      <h1 class="login-form-card__title">{{ titleText }}</h1>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="login-form__loader">
      <div class="spinner"></div>
      <p>Loading...</p>
    </div>

    <!-- Login Form -->
    <form v-show="!isLoading" class="login-form" @submit.prevent="handleSubmit">
      <!-- Username Field -->
      <div class="login-form__field">
        <label for="username" class="login-form__label">Username</label>
        <input
          id="username"
          v-model="username"
          type="text"
          class="login-form__input"
          placeholder="Enter your username"
          required
          :disabled="isLoading"
        />
      </div>

      <!-- Password Field -->
      <div class="login-form__field">
        <label for="password" class="login-form__label">Password</label>
        <input
          id="password"
          v-model="password"
          type="password"
          class="login-form__input"
          placeholder="Enter your password"
          required
          :disabled="isLoading"
        />
      </div>

      <!-- Forgot Password Link -->
      <div class="login-form__forgot">
        <!-- <a href="#" class="login-form__forgot-link">
          Forgot password <span class="login-form__forgot-link-text">Click here</span>
        </a> -->
          <router-link
            to="/forget-password"
            class="login-form__forgot-link"
          >
            Forgot password <span class="login-form__forgot-link-text">Click here</span>
          </router-link>
      </div>

      <!-- Error Message -->
      <div v-if="errorMessage" class="login-form__error">
        {{ errorMessage }}
      </div>

      <!-- Submit Button -->
      <button type="submit" class="login-form__button" :disabled="isLoading">
        {{ isLoading ? 'Logging in...' : 'Login' }}
      </button>
    </form>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { gql } from '@apollo/client/core'
import { usePathController } from '@/composables/usePathController'
import { checkCompanySSOEnabled } from '@/services/entityService'
import {useLogger} from "@/composables/useLogger"
import {useUserStore} from "@/store/user"
const userStore = useUserStore()
const {sendLog} = useLogger()

/**
 * Router Setup
 */
const router = useRouter()
const route = useRoute()
const { getLoginPath, goTo  } = usePathController()

/**
 * Component State
 */
const username = ref('')
const password = ref('')
const isLoading = ref(false)
const errorMessage = ref(route.query?.errorMessage || '')
const serviceName = ref('')
const organizationLogoUrl = ref('')
/**
 * Computed property for dynamic title
 */
const titleText = computed(() => {
  if (serviceName.value) {
    return `${serviceName.value} Login`
  }
  return 'Login to services'
})

/**
 * Computed property to determine logo box background
 * Uses white background if logo URL contains 'dark', otherwise uses dark background
 */
const logoBoxStyle = computed(() => {
  const isLogoTransparent = organizationLogoUrl.value.toLowerCase().includes('transparent')
  
  if (isLogoTransparent) {
    // White background for dark logos
    return {
      backgroundColor: '#2d3748',
      border: '2px solid #1a202c'
    }
  } else {
    // Dark background for light/regular logos
    return {
      backgroundColor: 'white',
      border: '2px solid #dee2e6'
    }
  }
})

/**
 * Load branding data from GraphQL API
 */
const loadBrandingData = async () => {
  let path = route.params && route.params.path || ""
  if (!path) {
    organizationLogoUrl.value = ""
    serviceName.value = ""
    return
  }

  if(typeof path === 'object') {
    path = path.join("/")
  }

  return checkCompanySSOEnabled(path)
    .then(({redirectUrl,name,logoUrl,source}) => {
      if(redirectUrl){
        pageCanbeLoaded(false)
        window.location.href=redirectUrl
      }
      else {
         organizationLogoUrl.value = logoUrl ? `https://${logoUrl}` : '',
         serviceName.value =  name || ''
        // return Promise.resolve({
        //   organizationLogo: logoUrl ? `https://${logoUrl}` : '',
        //   serviceName: name || ''
        // })
      }
    })
    .catch(error => {
      sendLog("error", "checkCompanySSOEnabled", {error, metadata: {path}})
      organizationLogoUrl.value = ""
      serviceName.value = ""
      // Return empty branding on error
    //   return Promise.resolve( {
    //     organizationLogoUrl.value = ""
    // serviceName.value = ""
    //   })
    })
}

/**
 * Load logo function - loads organization logo when available
 */
const loadLogo = async () => {
  try {
    await loadBrandingData()
    // const brandingData = await loadBrandingData()
    
    // // Update refs with loaded data
    // organizationLogoUrl.value = brandingData.organizationLogo
    // serviceName.value = brandingData.serviceName
  
  } catch (error) {
    sendLog("error", "LoginForm - loadLogo", {error})
  }
}

/**
 * Watch for route parameter changes and reload branding data
 */
watch(
  () => [route.params.path],
  () => {
    loadLogo()
  },
  { deep: true }
)

/**
 * Load branding data on component mount
 */
onMounted(() => {
  loadLogo()
})

/**
 * Page loading control function
 * @param {boolean} loaded - Whether the page has loaded
 */
function pageCanbeLoaded(loaded) {
  isLoading.value = !loaded
}

/**
 * Handle form submission
 */
const handleSubmit = async () => {
  // Clear previous error message
  errorMessage.value = ''
  
  // Set loading state
  pageCanbeLoaded(false)
  
  // Prepare login credentials
  const loginData = {
    username: username.value,
    password: password.value,
    organisation: '',
    company: ''
  }
  
  try {
    // Call the login service from composable
    await userStore.login(loginData)
 
    const [error, destination] = getLoginPath()
    errorMessage.value = ''
    goTo(destination)
  } catch (err) {
    // Handle unexpected errors
    errorMessage.value = err.message || 'An unexpected error occurred during login'
    pageCanbeLoaded(true)
  }
}
</script>

<style scoped>
.login-form-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  padding: 3rem;
  max-width: 450px;
  width: 100%;
  position: relative;
  z-index: 10;
}

.login-form-card__logo-box {
  /* Base styles - dynamic background applied via :style binding */
  padding: 2rem;
  margin: -3rem -3rem 2rem -3rem;
  border-radius: 16px 16px 0 0;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100px;
}

.login-form-card__logo-wrapper {
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.login-form-card__logo {
  max-width: 100%;
  max-height: 80px;
  height: auto;
  object-fit: contain;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1));
}

.login-form-card__header {
  margin-bottom: 2rem;
  text-align: left;
}

.login-form-card__title {
  font-size: 1.75rem;
  font-weight: 600;
  color: #1a202c;
  margin: 0 0 0.5rem 0;
}

.login-form-card__subtitle {
  font-size: 1rem;
  color: #4a5568;
  margin: 0;
  line-height: 1.5;
}

.login-form-card__subtitle-note {
  display: block;
  font-size: 0.75rem;
  color: #718096;
  font-style: italic;
  margin-top: 0.25rem;
}

/* Loading State */
.login-form__loader {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 0;
  gap: 1rem;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #e2e8f0;
  border-top-color: #4299e1;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.login-form__loader p {
  color: #4a5568;
  font-size: 0.875rem;
  margin: 0;
}

/* Form Styles */
.login-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.login-form__field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.login-form__label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #2d3748;
}

.login-form__input {
  padding: 0.875rem 1rem;
  font-size: 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f7fafc;
  transition: all 0.2s;
}

.login-form__input:focus {
  outline: none;
  border-color: #4299e1;
  background: white;
  box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
}

.login-form__input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.login-form__input::placeholder {
  color: #a0aec0;
}

.login-form__forgot {
  text-align: right;
  margin-top: -0.5rem;
}

.login-form__forgot-link {
  font-size: 0.875rem;
  color: #175A92;
  text-decoration: none;
  transition: color 0.2s;
}

.login-form__forgot-link:hover {
  color: #1C4673;
}

.login-form__forgot-link-text {
  font-weight: 500;
}

/* Error Message */
.login-form__error {
  padding: 0.75rem 1rem;
  background-color: #fed7d7;
  color: #c53030;
  border-radius: 8px;
  font-size: 0.875rem;
  border: 1px solid #fc8181;
}

/* Submit Button */
.login-form__button {
  padding: 1rem;
  font-size: 1rem;
  font-weight: 600;
  color: #1a202c;
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 0.5rem;
}

.login-form__button:hover:not(:disabled) {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.4);
}

.login-form__button:active:not(:disabled) {
  transform: translateY(0);
}

.login-form__button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Responsive Design */
@media (max-width: 768px) {
  .login-form-card {
    padding: 2rem 1.5rem;
    max-width: 100%;
  }

  .login-form-card__logo-box {
    padding: 1.5rem;
    margin: -2rem -1.5rem 1.5rem -1.5rem;
    min-height: 80px;
  }

  .login-form-card__logo {
    max-height: 60px;
  }

  .login-form-card__title {
    font-size: 1.5rem;
  }
}
</style>